import apiClient from './api';

/** Prefers the backend's own detail message (usually already specific),
 * same pattern as services/vendorDashboard.js's friendlyApiError(). */
function detailOrFallback(error, fallback) {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

/** Like detailOrFallback, but never returns FastAPI's 422 error array. */
function stringDetailOrFallback(error, fallback) {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export class AdminDashboardService {
  static async getMarketplace() {
    try {
      const response = await apiClient.get('/admin/dashboard/marketplace');
      return { success: true, dashboard: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load the marketplace dashboard') };
    }
  }
}

// R1C: email diagnosis (status: sent | not_configured | debug_mode | smtp_error).
export class AdminEmailService {
  static async sendTest() {
    try {
      const response = await apiClient.post('/admin/email/test');
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to run the email test') };
    }
  }
}

export class AdminVendorApplicationsService {
  static async list(params = {}) {
    try {
      const response = await apiClient.get('/admin/vendor-applications', { params });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load vendor applications') };
    }
  }

  static async get(id) {
    try {
      const response = await apiClient.get(`/admin/vendor-applications/${id}`);
      return { success: true, application: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load this application') };
    }
  }

  static async approve(id, { planId, contract, notes }) {
    try {
      const response = await apiClient.post(`/admin/vendor-applications/${id}/approve`, {
        plan_id: planId,
        contract,
        notes: notes || undefined,
      });
      return { success: true, application: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to approve this application') };
    }
  }

  static async reject(id, reason) {
    try {
      const response = await apiClient.post(`/admin/vendor-applications/${id}/reject`, { reason });
      return { success: true, application: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reject this application') };
    }
  }

  static async requestInfo(id, reason) {
    try {
      const response = await apiClient.post(`/admin/vendor-applications/${id}/request-info`, { reason });
      return { success: true, application: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to request more info') };
    }
  }

  // R1B.3 verification checklist
  static async saveVerification(id, checklist) {
    try {
      const response = await apiClient.put(`/admin/vendor-applications/${id}/verification`, checklist);
      return { success: true, verification: response.data };
    } catch (error) {
      return { success: false, error: stringDetailOrFallback(error, 'Failed to save the checklist') };
    }
  }

  // R1B.4 admin-assisted onboarding ("Add vendor"): multipart, optional uploads.
  static async addVendor(fields) {
    try {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        formData.append(key, value);
      });
      const response = await apiClient.post('/admin/vendor-applications/admin-create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: stringDetailOrFallback(error, 'Failed to add this vendor') };
    }
  }

  // R1C: fresh one-time link for WhatsApp/copy (older links stop working).
  static async shareInviteLink(id, channel) {
    try {
      const response = await apiClient.post(`/admin/vendor-applications/${id}/invite-link`, { channel });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: stringDetailOrFallback(error, 'Failed to create an invite link') };
    }
  }

  static async changeInviteEmail(id, email) {
    try {
      const response = await apiClient.put(`/admin/vendor-applications/${id}/invite-email`, { email });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: stringDetailOrFallback(error, 'Failed to change the email') };
    }
  }

  static async resendInvite(id) {
    try {
      const response = await apiClient.post(`/admin/vendor-applications/${id}/resend-invite`);
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: stringDetailOrFallback(error, 'Failed to resend the invite') };
    }
  }
}

export class AdminStoresService {
  static async getPaymentDetails(storeId) {
    try {
      const response = await apiClient.get(`/admin/stores/${storeId}/payment-details`);
      return { success: true, details: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load payment details') };
    }
  }

  static async updatePaymentDetails(storeId, payload) {
    try {
      const response = await apiClient.put(`/admin/stores/${storeId}/payment-details`, payload);
      return { success: true, details: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to save payment details') };
    }
  }

  static async list(params = {}) {
    try {
      const response = await apiClient.get('/admin/stores', { params });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load stores') };
    }
  }

  static async get(id) {
    try {
      const response = await apiClient.get(`/admin/stores/${id}`);
      return { success: true, store: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load this store') };
    }
  }

  static async getVendor360(id) {
    try {
      const response = await apiClient.get(`/admin/stores/${id}/vendor-360`);
      return { success: true, vendor360: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load this vendor\'s 360 view') };
    }
  }

  static async assignPlan(id, planId) {
    try {
      const response = await apiClient.put(`/admin/stores/${id}/plan`, null, { params: { plan_id: planId } });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to assign plan') };
    }
  }

  static async setOverride(id, featureKey, { access, limitValue, reason }) {
    try {
      const response = await apiClient.put(`/admin/stores/${id}/overrides/${featureKey}`, {
        access,
        limit_value: limitValue === '' || limitValue === undefined ? null : Number(limitValue),
        reason,
      });
      return { success: true, override: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to set feature override') };
    }
  }

  static async removeOverride(id, featureKey) {
    try {
      await apiClient.delete(`/admin/stores/${id}/overrides/${featureKey}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to remove feature override') };
    }
  }

  static async createContract(id, contract) {
    try {
      const response = await apiClient.post(`/admin/stores/${id}/contracts`, contract);
      return { success: true, contract: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to create the new deal') };
    }
  }

  static async suspend(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/stores/${id}/suspend`, { reason });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to suspend this store') };
    }
  }

  static async reactivate(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/stores/${id}/reactivate`, { reason });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reactivate this store') };
    }
  }

  static async terminate(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/stores/${id}/terminate`, { reason });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to terminate this store') };
    }
  }

  static async extendGrace(id, reason, graceUntil) {
    try {
      const response = await apiClient.post(`/admin/billing/stores/${id}/extend-grace`, {
        reason,
        grace_until: graceUntil,
      });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to extend grace') };
    }
  }
}

export class AdminPlansService {
  static async list() {
    try {
      const response = await apiClient.get('/admin/plans');
      return { success: true, plans: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load plans') };
    }
  }

  static async create(payload) {
    try {
      const response = await apiClient.post('/admin/plans', payload);
      return { success: true, plan: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to create plan') };
    }
  }

  static async update(id, payload) {
    try {
      const response = await apiClient.put(`/admin/plans/${id}`, payload);
      return { success: true, plan: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to update plan') };
    }
  }

  static async setFeature(planId, featureKey, { access, limitValue }) {
    try {
      const response = await apiClient.put(`/admin/plans/${planId}/features/${featureKey}`, {
        access,
        limit_value: limitValue === '' || limitValue === undefined ? null : Number(limitValue),
      });
      return { success: true, feature: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to set plan feature') };
    }
  }
}

export class AdminBillingService {
  static async listProofs(status) {
    try {
      const response = await apiClient.get('/admin/billing/proofs', { params: status ? { status } : {} });
      return { success: true, proofs: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load payment proofs') };
    }
  }

  static async approveProof(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/proofs/${id}/approve`, { reason });
      return { success: true, proof: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to approve this proof') };
    }
  }

  static async rejectProof(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/proofs/${id}/reject`, { reason });
      return { success: true, proof: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reject this proof') };
    }
  }

  static async listInvoices(storeId) {
    try {
      const response = await apiClient.get('/admin/billing/invoices', { params: storeId ? { store_id: storeId } : {} });
      return { success: true, invoices: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load invoices') };
    }
  }

  static async waiveInvoice(id, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/invoices/${id}/waive`, { reason });
      return { success: true, invoice: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to waive this invoice') };
    }
  }

  static async generateCommissionInvoices(period) {
    try {
      const response = await apiClient.post('/admin/billing/commission/generate-invoices', { period });
      return { success: true, invoices: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to generate commission invoices') };
    }
  }

  static async getEarningsTotals() {
    try {
      const response = await apiClient.get('/admin/billing/earnings/totals');
      return { success: true, totals: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load platform earnings totals') };
    }
  }
}

export class AdminCommissionService {
  static async getLedger(storeId) {
    try {
      const response = await apiClient.get('/admin/billing/commission/ledger', { params: { store_id: storeId } });
      return { success: true, entries: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, "Failed to load this store's ledger") };
    }
  }

  static async getEarnings(storeId, period) {
    try {
      const response = await apiClient.get('/admin/billing/commission/earnings', { params: { store_id: storeId, period } });
      return { success: true, earnings: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load earnings for this store') };
    }
  }

  static async getLowMargin() {
    try {
      const response = await apiClient.get('/admin/billing/commission/low-margin');
      return { success: true, products: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load low-margin products') };
    }
  }

  static async getStalledProofs() {
    try {
      const response = await apiClient.get('/admin/billing/commission/stalled-proofs');
      return { success: true, proofs: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load stalled proofs') };
    }
  }

  static async markReturned(vendorOrderId, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/vendor-orders/${vendorOrderId}/return`, { reason });
      return { success: true, result: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to mark this order returned') };
    }
  }

  static async addAdjustment(vendorOrderId, amount, reason) {
    try {
      const response = await apiClient.post(`/admin/billing/vendor-orders/${vendorOrderId}/commission-adjustment`, {
        amount, reason,
      });
      return { success: true, entry: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to add commission adjustment') };
    }
  }
}

export class AdminModerationService {
  static async listPendingProducts() {
    try {
      const response = await apiClient.get('/admin/products/pending');
      return { success: true, products: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load pending products') };
    }
  }

  static async approveProduct(id, reason) {
    try {
      const response = await apiClient.post(`/admin/products/${id}/approve`, { reason: reason || undefined });
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to approve this product') };
    }
  }

  static async rejectProduct(id, reason) {
    try {
      const response = await apiClient.post(`/admin/products/${id}/reject`, { reason });
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reject this product') };
    }
  }
}

export class AdminTaxonomyService {
  static async listCategories() {
    try {
      const response = await apiClient.get('/admin/categories');
      return { success: true, categories: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load categories') };
    }
  }

  static async createCategory({ name, parentId, sortOrder, involvesMinors = false, homeVisit = false }) {
    try {
      const response = await apiClient.post('/admin/categories', {
        name, parent_id: parentId ?? null, sort_order: sortOrder ?? 0, involves_minors: involvesMinors, home_visit: homeVisit,
      });
      return { success: true, category: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to create category') };
    }
  }

  static async updateCategory(id, payload) {
    try {
      const response = await apiClient.put(`/admin/categories/${id}`, payload);
      return { success: true, category: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to update category') };
    }
  }

  static async moveCategory(id, parentId) {
    try {
      const response = await apiClient.post(`/admin/categories/${id}/move`, { parent_id: parentId ?? null });
      return { success: true, category: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to move category') };
    }
  }

  static async listBrands() {
    try {
      const response = await apiClient.get('/admin/brands');
      return { success: true, brands: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load brands') };
    }
  }

  static async createBrand(name) {
    try {
      const response = await apiClient.post('/admin/brands', { name });
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to create brand') };
    }
  }

  static async approveBrand(id) {
    try {
      const response = await apiClient.post(`/admin/brands/${id}/approve`);
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to approve brand') };
    }
  }

  // R2.3: reason required; a store-private brand goes back to PRIVATE.
  static async rejectBrand(id, reason) {
    try {
      const response = await apiClient.post(`/admin/brands/${id}/reject`, { reason });
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reject brand') };
    }
  }

  static async updateBrand(id, payload) {
    try {
      const response = await apiClient.put(`/admin/brands/${id}`, payload);
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to update brand') };
    }
  }

  static async listTaxonomyRequests(status) {
    try {
      const response = await apiClient.get('/admin/taxonomy-requests', { params: status ? { status } : {} });
      return { success: true, requests: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load taxonomy requests') };
    }
  }

  static async approveTaxonomyRequest(id) {
    try {
      const response = await apiClient.post(`/admin/taxonomy-requests/${id}/approve`);
      return { success: true, request: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to approve this request') };
    }
  }

  static async rejectTaxonomyRequest(id, reason) {
    try {
      const response = await apiClient.post(`/admin/taxonomy-requests/${id}/reject`, { reason });
      return { success: true, request: response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to reject this request') };
    }
  }
}

export class AdminAuditLogsService {
  static async list(params = {}) {
    try {
      const response = await apiClient.get('/admin/audit-logs', { params });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: detailOrFallback(error, 'Failed to load audit logs') };
    }
  }
}
